import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { ShieldCheck, Lock, Loader2, Check, AlertTriangle } from "lucide-react";
import { REDIRECTS, mockCheckAccess, mockVerify } from "../mock";
import "./Checkpoint.css";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Toggle to use backend once implemented. Frontend-only preview uses mock.
const USE_BACKEND = true;

const STATE = {
  LOADING: "loading",
  READY: "ready",
  BLOCKED: "blocked",
  REDIRECTING: "redirecting",
};

export default function Checkpoint() {
  const [state, setState] = useState(STATE.LOADING);
  const [checkState, setCheckState] = useState("idle"); // idle | verifying | verified
  const [blockMinutes, setBlockMinutes] = useState(40);
  const [honeypot, setHoneypot] = useState("");
  const cardRef = useRef(null);
  const redirectedRef = useRef(false);

  const doRedirect = (url) => {
    if (redirectedRef.current) return;
    redirectedRef.current = true;
    window.location.href = url;
  };

  // Access check on mount
  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        let data;
        if (USE_BACKEND) {
          const res = await axios.post(`${API}/check-access`, {
            userAgent: navigator.userAgent,
            screen: `${window.screen.width}x${window.screen.height}`,
            webdriver: !!navigator.webdriver,
          });
          data = res.data;
        } else {
          data = await mockCheckAccess();
        }
        if (cancelled) return;

        if (data.action === "bot") {
          setState(STATE.REDIRECTING);
          doRedirect(data.redirect || REDIRECTS.bot);
          return;
        }
        if (data.action === "blocked") {
          setBlockMinutes(data.minutes || 40);
          setState(STATE.BLOCKED);
          setTimeout(() => doRedirect(data.redirect || REDIRECTS.block), 3000);
          return;
        }
        setState(STATE.READY);
      } catch (e) {
        // On error, fail open to ready so real users aren't stuck
        if (!cancelled) setState(STATE.READY);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCheckbox = async () => {
    if (checkState !== "idle") return;
    // Honeypot => bot
    if (honeypot) {
      doRedirect(REDIRECTS.bot);
      return;
    }
    setCheckState("verifying");
    try {
      let data;
      if (USE_BACKEND) {
        const res = await axios.post(`${API}/verify`, {
          honeypot,
          userAgent: navigator.userAgent,
          webdriver: !!navigator.webdriver,
        });
        data = res.data;
      } else {
        data = await mockVerify();
      }

      if (data.action === "bot") {
        doRedirect(data.redirect || REDIRECTS.bot);
        return;
      }
      if (data.action === "blocked") {
        setBlockMinutes(data.minutes || 40);
        setState(STATE.BLOCKED);
        setTimeout(() => doRedirect(data.redirect || REDIRECTS.block), 3000);
        return;
      }
      // success
      setCheckState("verified");
    } catch (e) {
      setCheckState("verified");
    }
  };

  const handleNext = () => {
    if (checkState !== "verified") return;
    setState(STATE.REDIRECTING);
    doRedirect(REDIRECTS.success);
  };

  return (
    <div className="gd-wrap" data-testid="checkpoint-page">
      <div className="gd-grid" aria-hidden="true" />
      <div className="gd-glow" aria-hidden="true" />

      <div className="gd-card" data-testid="checkpoint-card" ref={cardRef}>
        <div className="gd-brand">
          <span className="gd-brand-mark">
            <ShieldCheck size={20} strokeWidth={2.4} />
          </span>
        </div>

        {state === STATE.BLOCKED ? (
          <div className="gd-blocked" data-testid="blocked-state">
            <span className="gd-blocked-icon">
              <AlertTriangle size={22} strokeWidth={2.4} />
            </span>
            <h1 className="gd-title">Access temporarily blocked</h1>
            <p className="gd-error" data-testid="block-message">
              Too many attempts. Try again in {blockMinutes} minutes.
            </p>
            <p className="gd-sub gd-sub-muted">Redirecting you shortly…</p>
          </div>
        ) : (
          <>
            <h1 className="gd-title" data-testid="checkpoint-title">
              One more step before you continue
            </h1>
            <p className="gd-sub">Checking safety of environment</p>

            {/* Honeypot: hidden from humans, bots tend to fill it */}
            <input
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              className="gd-honeypot"
              data-testid="honeypot-field"
              type="text"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />

            <div className="gd-controls">
              <button
                type="button"
                className={`gd-check ${checkState}`}
                data-testid="verify-checkbox"
                aria-pressed={checkState === "verified"}
                onClick={handleCheckbox}
                disabled={checkState !== "idle"}
              >
                <span className="gd-check-box">
                  {checkState === "verifying" && (
                    <Loader2 className="gd-spin" size={16} />
                  )}
                  {checkState === "verified" && <Check size={16} strokeWidth={3} />}
                </span>
                <span className="gd-check-label">
                  {checkState === "idle" && "I'm human — verify me"}
                  {checkState === "verifying" && "Verifying…"}
                  {checkState === "verified" && "Verified"}
                </span>
              </button>

              <button
                type="button"
                className="gd-next"
                data-testid="next-button"
                disabled={checkState !== "verified"}
                onClick={handleNext}
              >
                Next
              </button>
            </div>
          </>
        )}

        <div className="gd-foot">
          <Lock size={12} strokeWidth={2} />
          <span>Secured environment check</span>
        </div>
      </div>
    </div>
  );
}
